import React, { useEffect, useState } from 'react';
import moment, { MomentInput } from 'moment';
import { useAppSelector } from '../../../store/hooks';
import { selectSlice as selectStudiesSlice } from '../../../store/slices/studies';
import { getExports } from '../../../services/exports.service';
import { Export } from '../../../utils/models';
import {
  GridToolbarContainer,
  GridLoadIcon,
  GridLoadingOverlay,
  GridColDef,
  GridValueGetterParams,
  GridValueFormatterParams,
  GridCellParams,
} from '@mui/x-data-grid';
import { Tooltip } from "@mui/material";
import { BaseTable, DownloadFileButton } from '../../../common/components';
import { DownloadType } from '../../../services/file.service';

const statusDisplay: any = {
  ready: 'Procesando',
  exporting: 'Procesando',
  cancelled: 'Cancelado',
  complete: 'Completo',
};

const exportType: Record<string, string> = {
  json: 'Automática',
  csv: 'Manual',
};

function formatStatus (params: GridValueFormatterParams): string {
  const k: string = params.value as string;
  return statusDisplay[k] || '';
}

function formatType (params: GridValueFormatterParams): string {
  const k: string = params.value as string;
  return exportType[k] || '';
}

function formatDate (params: GridValueFormatterParams): string {
  const v: MomentInput = params.value as string;
  const m = moment(v);
  return m.format('DD/MM/YYYY HH:mm');
}

function getCompoundName (params: GridValueGetterParams):string {
  const range = params?.row?.dateRange;
  if (!range) return '';
  const start = moment(range?.start).format('DD/MM/YY');
  const end = moment(range?.end).format('DD/MM/YY');
  return `Nutrento ${start} - ${end}`;
}

const cols: GridColDef[] = [
  {
    field: 'surveyId',
    headerName: 'Intervalo de exportación',
    valueGetter: getCompoundName,
    flex: 1,
    disableColumnMenu: true,
    renderCell: ({ value }: GridCellParams) => (
      <Tooltip title={value || ''} >
        <span>{value}</span>
      </Tooltip>
    ),
  },
  {
    field: 'format',
    type: 'string',
    headerName: 'Tipo',
    description: 'Tipo de exportación realizada',
    valueFormatter: formatType,
    flex: 0.5,
    disableColumnMenu: true,
  },
  {
    field: 'status',
    headerName: 'Estado',
    description: 'Muestra el estado en el que se encuentra el procesamiento del archivo.',
    valueFormatter: formatStatus,
    flex: 0.5,
    disableColumnMenu: true,
  },
  {
    field: 'createdAt',
    headerName: 'Fecha de creación',
    type: 'dateTime',
    valueFormatter: formatDate,
    flex: 0.75,
    disableColumnMenu: true,
  },
  {
    field: 'totalInteractions',
    headerName: 'Respuestas',
    flex: 0.75,
    disableColumnMenu: true,
    align: 'right',
    headerAlign: 'right',
  },
  {
    field: 'id',
    headerName: ' ',
    flex: 0.25,
    disableColumnMenu: true,
    renderCell: ({ row, value: id }: GridCellParams) => (
      <Tooltip title="Descargar respuestas" >
        <div>
          <DownloadFileButton
            downloadType={DownloadType.EXPORT}
            downloadId={id as string}
            downloadFileName={`Respuestas_${getCompoundName({ row } as GridValueGetterParams)}.${row?.format ?? '.csv'}`}
            disabled={!(row?.totalInteractions > 0)}
          />
        </div>
      </Tooltip>
    ),
    sortable: false,
  },
];

const ToolbarIconStyles: React.CSSProperties = {
  lineHeight: 1,
  background: 'none',
  color: "primary.main",
  marginRight: '1em',
  border:'none'
};

const ExportsTableToolbar = (toolbarProps: any) => {
  return (
    <GridToolbarContainer
      style={{ justifyContent: 'space-between' }}
    >
      <b style={{ paddingLeft: '12px' }}>{toolbarProps?.tableTitle}</b>
      <div>
        <button
          onClick={toolbarProps?.onReload}
          style={ToolbarIconStyles}
        >
          <GridLoadIcon fontSize="small" />
        </button>
      </div>
    </GridToolbarContainer>
  );
}

const ExportsTable = () => {

  const [exports, setExports] = useState<Export[]>([]);
  const [loading, setLoading] = useState(true);
  const studies = useAppSelector(selectStudiesSlice);

  async function loadExports (): Promise<void> {
    setLoading(true);
    try {
      const exportList = await getExports(studies.selected?._id);
      if (exportList) {
        setExports(exportList);
      }
    } catch (err: unknown) {
      console.log('Ocurrió un problema al cargar los registros.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadExports();
  }, [studies.selected]);

  return (
      <BaseTable
        density="compact"
        getRowId={r => r._id}
        columns={cols}
        rows={exports}
        autoHeight
        disableRowSelectionOnClick
        disableDensitySelector
        disableColumnSelector
        initialState={{
          pagination: { paginationModel: { pageSize: 25 } }
        }}
        initialSortModel={[{
          field: 'createdAt',
          sort: 'desc'
        }]}
        loading={loading}
        localeText={{
          noRowsLabel: 'No se encontraron registros.',
          toolbarExport: '',
          toolbarFilters: '',
          toolbarExportCSV: 'Descargar CSV',
          toolbarExportLabel: 'Exportar',
          MuiTablePagination: {
            labelRowsPerPage: 'Exportaciones por página:',
            labelDisplayedRows: ({ from, to, count }) => `${from} - ${to} de ${count}`,
          }
        }}
        pageSizeOptions={[15, 25]}
        slots={{
          toolbar: ExportsTableToolbar,
          loadingOverlay: GridLoadingOverlay,
        }}
        slotProps={{
          toolbar: {
            onReload: loadExports,
            tableTitle: 'Archivos generados'
          }
        }}
      />
  );
}

export default ExportsTable;
export {
  ExportsTable
};